import { css } from "@emotion/react";
import { Button } from "@mui/material";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import { match } from "ts-pattern";

import DatabasePanel from "~/components/category/[id]/database/database-panel";
import { PC_PANEL_WIDTH } from "~/components/category/[id]/database/database-panel/lib";
import SingleSelectInput from "~/components/common/SingleSelectInput";
import { mediaQueryIsDesktop, mediaQueryIsMobile } from "~/lib/client/design";
import { useActivityLogger } from "~/lib/client/logger/useActivityLogger";
import "~/lib/monkeypatch";
import { useCategoryDetailContext } from "~/pages/category/[id]";

const DATABASE_TAB_SORTS = [
  {
    value: "LAST_RESPONSE",
    label: "投稿日順",
  },
  { value: "LATEST", label: "作成日順" },
  {
    value: "RESPONSE_COUNT",
    label: "投稿数順",
  },
] as const;

const DatabaseTab = () => {
  const router = useRouter();
  const {
    props: {
      category,
      category: { forms },
    },
  } = useCategoryDetailContext();
  const logger = useActivityLogger();

  const [selectedSort, setSelectedSort] =
    useState<(typeof DATABASE_TAB_SORTS)[number]["value"]>("LAST_RESPONSE");

  const sortedForms = useMemo(() => {
    return forms
      .toSorted((a, b) =>
        match(selectedSort)
          .with(
            "LATEST",
            () => dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix()
          )
          .with("RESPONSE_COUNT", () => b.responses.length - a.responses.length)
          .with(
            "LAST_RESPONSE",
            () =>
              dayjs(b.lastResponseAt ?? 0).unix() -
              dayjs(a.lastResponseAt ?? 0).unix()
          )
          .exhaustive()
      )
      .compact();
  }, [forms, selectedSort]);

  const handleCreateDatabase = async () => {
    if (!logger) return;

    await logger({
      actionName: "click-create-database",
      pageName: "categoryDetail",
    });
    await router.push(`/database/create?category_id=${category.id}`);
  };

  if (!forms || forms.length === 0) {
    return (
      <Button onClick={handleCreateDatabase} variant={"contained"}>
        データベースを作る
      </Button>
    );
  }

  return (
    <div css={rootStyle}>
      <div css={buttonListContainerStyle}>
        <div id={"sort-chip-list"} css={sortChipListContainerStyle}>
          {DATABASE_TAB_SORTS.map((sort, index) => (
            <SingleSelectInput
              label={sort.label}
              selected={sort.value === selectedSort}
              setSelected={() => setSelectedSort(sort.value)}
              key={index}
            />
          ))}
        </div>
        <div id={"create-database-button"} css={buttonContainerStyle}>
          <Button
            onClick={() =>
              router.push("/database/create?category_id=" + category.id)
            }
            variant={"contained"}
          >
            データベースを作る
          </Button>
        </div>
      </div>
      <div css={componentsStyle}>
        {sortedForms.map((form, idx) => (
          <DatabasePanel
            key={idx}
            database={{ ...form, postSize: form.responses.length }}
          />
        ))}
      </div>
    </div>
  );
};

const rootStyle = css`
  margin: 1rem 0.5rem;
  padding-top: 2rem;
  padding-bottom: 2rem;

  ${mediaQueryIsMobile(css`
    border-radius: 0.5rem;
    margin-top: 0.5rem;
    padding: 0.5rem;
  `)};

  ${mediaQueryIsDesktop(css`
    border-radius: 2rem;
  `)};
`;

const buttonContainerStyle = css`
  margin-top: 1rem;
  margin-bottom: 1rem;

  display: flex;
  justify-content: flex-end;
`;

const componentsStyle = css`
  width: 100%;
  display: grid;

  ${mediaQueryIsMobile(css`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
  `)};

  ${mediaQueryIsDesktop(css`
    grid-template-columns: repeat(auto-fill, ${PC_PANEL_WIDTH}px);
    justify-content: space-evenly;
    gap: 0.5rem;
  `)};
`;

const buttonListContainerStyle = css`
  display: grid;

  // sort-chip-list は1段目で左寄せ
  // create-database-button 2段目で右寄せ
  ${mediaQueryIsMobile(css`
    grid-template-columns: 1fr;
    gap: 0.5rem;

    #sort-chip-list {
      grid-row: 1;
    }

    #create-database-button {
      grid-row: 2;
      justify-content: flex-end;
    }
  `)};

  ${mediaQueryIsDesktop(css`
    margin-left: 1rem;
    margin-right: 1rem;

    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    justify-content: space-between;
  `)};
`;

const sortChipListContainerStyle = css`
  display: flex;
  gap: 0.5rem;
  flex-flow: row wrap;
  align-items: center;
`;

export default DatabaseTab;
